import { useState, type FormEvent } from "react";
import type { ScenarioRow, SharePermissionName } from "@shared/schemas/scenario";
import { Button } from "@client/components/Button";
import { SelectField, TextField } from "@client/components/Field";
import { Modal } from "@client/components/Modal";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { useScenario, useShareScenario, useUnshareScenario } from "@client/hooks/useScenarios";
import { describeError } from "@client/lib/errorMessage";

/**
 * Who else can see one scenario.
 *
 * The list of existing shares comes from the scenario itself rather than the row
 * — the row carries a count, not the people — so this fetches the detail when it
 * opens. That is one request per open, on a dialog, which is the right place to
 * spend one.
 *
 * Sharing a PRIVATE scenario promotes it to SHARED on the server, because a
 * share on a private scenario grants nothing. The dialog says so rather than
 * letting someone add three people to a scenario none of them can open.
 */
export function ShareDialog({
  scenario,
  open,
  onClose,
}: {
  scenario: ScenarioRow | null;
  open: boolean;
  onClose: () => void;
}) {
  const detail = useScenario(open && scenario ? scenario.id : undefined);
  const share = useShareScenario(scenario?.id ?? "");
  const unshare = useUnshareScenario(scenario?.id ?? "");

  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState<SharePermissionName>("VIEW");

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (share.isPending || !scenario) return;
    share.mutate(
      { email: email.trim().toLowerCase(), permission },
      { onSuccess: () => setEmail("") },
    );
  }

  const shares = detail.data?.shares ?? [];
  const visibility = detail.data?.visibility ?? scenario?.visibility;

  return (
    <Modal
      open={open}
      title={scenario ? `Share “${scenario.name}”` : "Share"}
      description="People you share with must already be on the allow list — sharing does not grant access to the portal itself."
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Done
        </Button>
      }
    >
      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        <div className="grid gap-3 sm:grid-cols-[1fr_9rem]">
          <TextField
            label="Email"
            type="email"
            required
            inputMode="email"
            autoComplete="off"
            placeholder="someone@dss.mv"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <SelectField
            label="Permission"
            value={permission}
            onChange={(event) => setPermission(event.target.value as SharePermissionName)}
          >
            <option value="VIEW">Can view</option>
            <option value="EDIT">Can edit</option>
          </SelectField>
        </div>

        {visibility === "PRIVATE" && (
          <p className="text-[11px] text-ink-soft">
            This scenario is private. Sharing it will make it visible to the people you name here.
          </p>
        )}

        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={share.isPending || email.trim().length === 0}>
            {share.isPending ? "Sharing…" : "Share"}
          </Button>
        </div>

        {share.isError && (
          <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
            {describeError(share.error)}
          </p>
        )}
      </form>

      <div className="flex flex-col gap-2 border-t border-line pt-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-ink-soft">Shared with</h3>

        {detail.isPending && <LoadingBlock rows={1} label="Loading shares" />}
        {detail.isError && (
          <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />
        )}

        {detail.isSuccess && shares.length === 0 && (
          <p className="text-xs text-ink-soft">Nobody yet.</p>
        )}

        <ul className="flex flex-col gap-1.5">
          {shares.map((entry) => (
            <li
              key={entry.userId}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-chip-bg px-3 py-2"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-xs font-semibold text-ink">
                  {entry.user.fullName}
                </span>
                <span className="truncate text-[11px] text-ink-soft">{entry.user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold text-ink-soft">
                  {entry.permission === "EDIT" ? "Can edit" : "Can view"}
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={unshare.isPending}
                  onClick={() => unshare.mutate(entry.userId)}
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>

        {unshare.isError && (
          <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
            {describeError(unshare.error)}
          </p>
        )}
      </div>
    </Modal>
  );
}
